import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const API = process.env.REACT_APP_API_URL || "";
const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "").split(",").map(e => e.trim().toLowerCase());

function isAdmin(user) {
  return user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
}

// ── Quill toolbar config ────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ heading: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    ["clean"],
  ],
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "code-block", "link", "image",
];

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

// ── Inline styles for Quill in dark theme ───────────────────────────────────
const quillDarkStyles = `
  .ql-container { background:#1e1e32; color:#e2e2ef; border-color:#32324a !important; border-radius:0 0 8px 8px; min-height:240px; }
  .ql-toolbar { background:#16162a; border-color:#32324a !important; border-radius:8px 8px 0 0; }
  .ql-toolbar .ql-stroke { stroke:#a0a0c0; }
  .ql-toolbar .ql-fill { fill:#a0a0c0; }
  .ql-toolbar button:hover .ql-stroke { stroke:#8b6ff5; }
  .ql-toolbar button.ql-active .ql-stroke { stroke:#8b6ff5; }
  .ql-toolbar .ql-picker-label { color:#a0a0c0; }
  .ql-editor.ql-blank::before { color:#585880; font-style:normal; }
  .ql-editor a { color:#8b6ff5; }
  .ql-editor blockquote { border-left:3px solid #8b6ff5; margin-left:0; padding-left:1em; color:#a0a0c0; }
  .ql-editor pre.ql-syntax { background:#0e0e18; padding:.5em; border-radius:6px; }
`;

// ╔══════════════════════════════════════════════════════╗
// ║  Composant principal Blog                            ║
// ╚══════════════════════════════════════════════════════╝
export default function Blog({ user }) {
  const [view, setView] = useState("list"); // "list" | "article" | "editor"
  const [posts, setPosts] = useState([]);
  const [current, setCurrent] = useState(null); // article en vue
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const admin = isAdmin(user);
  const token = localStorage.getItem("sensoria_token");

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  // ── Charger la liste ──────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/blog`, { headers });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      setPosts(data.posts || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Ouvrir un article ─────────────────────────────────
  const openArticle = async (slug) => {
    try {
      const r = await fetch(`${API}/api/blog/${slug}`, { headers });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      setCurrent(data.post);
      setView("article");
      window.scrollTo(0, 0);
    } catch (e) { setErr(e.message); }
  };

  // ── Supprimer un article (admin) ──────────────────────
  const deletePost = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await fetch(`${API}/api/blog/${id}`, { method: "DELETE", headers });
    loadList();
    if (view === "article") setView("list");
  };

  // ── Vues ──────────────────────────────────────────────
  if (view === "editor") {
    return (
      <BlogEditor
        post={current}
        token={token}
        onSave={() => { loadList(); setView("list"); setCurrent(null); }}
        onCancel={() => { setView(current ? "article" : "list"); }}
      />
    );
  }

  if (view === "article" && current) {
    return (
      <ArticleView
        post={current}
        admin={admin}
        onBack={() => setView("list")}
        onEdit={() => setView("editor")}
        onDelete={() => deletePost(current.id)}
      />
    );
  }

  // LIST
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <style>{quillDarkStyles}</style>

      {/* En-tête */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-sensoria-text">📰 Blog</h1>
          <p className="mt-1 text-sm text-sensoria-text-dim">Articles et actualités de la communauté</p>
        </div>
        {admin && (
          <button
            onClick={() => { setCurrent(null); setView("editor"); }}
            className="sensoria-btn flex-shrink-0 px-4 py-2 text-sm"
          >
            + Nouvel article
          </button>
        )}
      </div>

      {loading && <p className="text-center text-sensoria-text-dim">Chargement…</p>}
      {err && <p className="rounded-lg bg-red-900/30 p-3 text-sm text-red-300">⚠ {err}</p>}

      {!loading && !err && posts.length === 0 && (
        <div className="sensoria-card py-12 text-center">
          <p className="text-4xl">✍️</p>
          <p className="mt-2 text-sensoria-text-dim">Aucun article pour l'instant.</p>
          {admin && (
            <button onClick={() => { setCurrent(null); setView("editor"); }} className="sensoria-btn mt-4 px-5 py-2 text-sm">
              Rédiger le premier article
            </button>
          )}
        </div>
      )}

      {posts.map(post => (
        <PostCard
          key={post.id}
          post={post}
          admin={admin}
          onOpen={() => openArticle(post.slug)}
          onEdit={() => { setCurrent(post); setView("editor"); }}
          onDelete={() => deletePost(post.id)}
        />
      ))}
    </div>
  );
}

// ── Card article dans la liste ───────────────────────────────────────────────
function PostCard({ post, admin, onOpen, onEdit, onDelete }) {
  return (
    <article className="sensoria-card overflow-hidden cursor-pointer group" onClick={onOpen}>
      {post.cover_url && (
        <div className="relative h-44 overflow-hidden bg-sensoria-bg">
          <img
            src={post.cover_url}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {post.status === "draft" && (
            <span className="absolute top-2 right-2 rounded bg-yellow-600 px-2 py-0.5 text-xs font-semibold text-white">Brouillon</span>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold text-sensoria-text group-hover:text-sensoria-accent transition-colors">
            {post.title}
          </h2>
          {admin && (
            <div className="flex flex-shrink-0 gap-1" onClick={e => e.stopPropagation()}>
              <button
                onClick={onEdit}
                className="rounded px-2 py-1 text-xs text-sensoria-text-dim hover:bg-sensoria-border hover:text-sensoria-text"
              >✏️</button>
              <button
                onClick={onDelete}
                className="rounded px-2 py-1 text-xs text-sensoria-text-dim hover:bg-red-900/40 hover:text-red-300"
              >🗑</button>
            </div>
          )}
        </div>
        {post.excerpt && <p className="mt-2 line-clamp-2 text-sm text-sensoria-text-dim">{post.excerpt}</p>}
        <p className="mt-3 text-xs text-sensoria-text-dim">{fmtDate(post.created_at)}</p>
      </div>
    </article>
  );
}

// ── Vue article complet ──────────────────────────────────────────────────────
function ArticleView({ post, admin, onBack, onEdit, onDelete }) {
  return (
    <div className="mx-auto max-w-3xl">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-sensoria-text-dim hover:text-sensoria-text">
        ← Retour au blog
      </button>

      {post.cover_url && (
        <div className="mb-6 h-56 overflow-hidden rounded-xl bg-sensoria-bg">
          <img src={post.cover_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="sensoria-card p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-bold text-sensoria-text">{post.title}</h1>
          {admin && (
            <div className="flex flex-shrink-0 gap-2">
              <button onClick={onEdit} className="sensoria-btn px-3 py-1.5 text-xs">✏️ Modifier</button>
              <button
                onClick={onDelete}
                className="rounded-lg border border-red-700 px-3 py-1.5 text-xs text-red-400 hover:bg-red-900/30"
              >🗑 Supprimer</button>
            </div>
          )}
        </div>

        <p className="mt-1 text-xs text-sensoria-text-dim">
          {fmtDate(post.created_at)}
          {post.status === "draft" && <span className="ml-2 rounded bg-yellow-700 px-1.5 py-0.5 text-yellow-200">Brouillon</span>}
        </p>

        {/* Contenu HTML rendu */}
        <div
          className="prose-blog mt-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </div>

      {/* Styles pour le contenu HTML du blog */}
      <style>{`
        .prose-blog { color:#c0c0e0; line-height:1.75; }
        .prose-blog h1,.prose-blog h2,.prose-blog h3 { color:#e2e2ef; font-weight:600; margin:1.5em 0 .5em; }
        .prose-blog h1 { font-size:1.6rem; }
        .prose-blog h2 { font-size:1.3rem; }
        .prose-blog h3 { font-size:1.1rem; }
        .prose-blog p { margin:.75em 0; }
        .prose-blog a { color:#8b6ff5; text-decoration:underline; }
        .prose-blog blockquote { border-left:3px solid #8b6ff5; margin:1em 0; padding:.5em 1em; color:#a0a0c0; background:#16162a; border-radius:0 6px 6px 0; }
        .prose-blog code { background:#0e0e18; padding:.15em .4em; border-radius:4px; font-size:.9em; color:#c0c0e0; }
        .prose-blog pre { background:#0e0e18; padding:1em; border-radius:8px; overflow-x:auto; }
        .prose-blog ul,.prose-blog ol { margin:.75em 0 .75em 1.5em; }
        .prose-blog li { margin:.25em 0; }
        .prose-blog img { max-width:100%; border-radius:8px; margin:1em 0; }
        .prose-blog strong { color:#e2e2ef; }
      `}</style>
    </div>
  );
}

// ── Éditeur admin (créer / modifier) ────────────────────────────────────────
function BlogEditor({ post, token, onSave, onCancel }) {
  const [title, setTitle] = useState(post?.title || "");
  const [excerpt, setExcerpt] = useState(post?.excerpt || "");
  const [content, setContent] = useState(post?.content || "");
  const [coverUrl, setCoverUrl] = useState(post?.cover_url || "");
  const [status, setStatus] = useState(post?.status || "draft");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const API = process.env.REACT_APP_API_URL || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const uploadCover = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    try {
      const r = await fetch(`${API}/api/photos/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok && data.url) setCoverUrl(data.url);
      else setErr("Erreur upload image.");
    } catch { setErr("Erreur upload."); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) { setErr("Titre et contenu requis."); return; }
    setSaving(true); setErr(null);
    const method = post ? "PUT" : "POST";
    const url = post ? `${API}/api/blog/${post.id}` : `${API}/api/blog`;
    try {
      const r = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({ title, excerpt, content, cover_url: coverUrl, status }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      onSave(data.post);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <style>{quillDarkStyles}</style>

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-sensoria-text">
          {post ? "✏️ Modifier l'article" : "✍️ Nouvel article"}
        </h2>
        <button onClick={onCancel} className="text-sm text-sensoria-text-dim hover:text-sensoria-text">
          ✕ Annuler
        </button>
      </div>

      {err && <p className="rounded-lg bg-red-900/30 p-3 text-sm text-red-300">⚠ {err}</p>}

      <div className="sensoria-card space-y-4 p-5">
        {/* Titre */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-sensoria-text-dim uppercase tracking-wide">Titre</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Titre de l'article"
            className="sensoria-input w-full text-base font-semibold"
          />
        </div>

        {/* Extrait */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-sensoria-text-dim uppercase tracking-wide">Extrait (affiché dans la liste)</label>
          <textarea
            value={excerpt}
            onChange={e => setExcerpt(e.target.value)}
            rows={2}
            placeholder="Résumé court de l'article…"
            className="sensoria-input w-full resize-none text-sm"
          />
        </div>

        {/* Image de couverture */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-sensoria-text-dim uppercase tracking-wide">Image de couverture</label>
          <div className="flex flex-wrap items-center gap-3">
            {coverUrl && (
              <div className="relative h-24 w-40 overflow-hidden rounded-lg">
                <img src={coverUrl} alt="Couverture" className="h-full w-full object-cover" />
                <button
                  onClick={() => setCoverUrl("")}
                  className="absolute top-1 right-1 rounded-full bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-red-700"
                >✕</button>
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-lg border border-sensoria-border px-4 py-2 text-sm text-sensoria-text-dim hover:border-sensoria-accent hover:text-sensoria-accent disabled:opacity-50"
            >
              {uploading ? "Upload…" : coverUrl ? "Changer l'image" : "📷 Ajouter une image"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadCover(e.target.files[0])} />
          </div>
        </div>

        {/* WYSIWYG */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-sensoria-text-dim uppercase tracking-wide">Contenu</label>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Rédigez votre article ici…"
          />
        </div>

        {/* Statut + sauvegarde */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-sensoria-border">
          <div className="flex items-center gap-3">
            <label className="text-sm text-sensoria-text-dim">Statut :</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="sensoria-input text-sm"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="rounded-lg border border-sensoria-border px-4 py-2 text-sm text-sensoria-text-dim hover:bg-sensoria-border">
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="sensoria-btn px-5 py-2 text-sm disabled:opacity-50"
            >
              {saving ? "Sauvegarde…" : post ? "Mettre à jour" : "Publier"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
